import React, { useState } from "react";
import styles from "./MassPriceUpdate.module.scss";
import XIconThemed from "@src/assets/general-icons/XIconThemed";
import { Button, ButtonVariant } from "../button/Button";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import helperStyles from "@src/components/helpers.module.scss";
import { clx } from "../utils/stringUtils";
import { IColumn, VGrid } from "../vGrid/VGrid";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import _ from "lodash";
import { getInventoryGroupKey } from "@src/components/utils/inventoryUtils";
import { formatCurrency } from "../utils/numberUtils";
import MinusIconThemed from "@src/assets/general-icons/MinusIconThemed";
import PlusIconThemed from "@src/assets/general-icons/PlusIconThemed";
import { pricingActions } from "@src/store";
import { toast } from "sonner";
import { groupSeatNumbersWithRange } from "../utils/columnUtils";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";

type TMassPriceUpdate = {
	handleClose?: () => void;
};

type TMassPriceUpdateMode = "amount" | "percentage" | "fixedPrice";

const MassPriceUpdate: React.FC<TMassPriceUpdate> = ({ handleClose }) => {
	const [amount, setAmount] = useState<string>("1");
	const [mode, setMode] = useState<TMassPriceUpdateMode>("amount");
	const dispatch = useAppDispatch();

	const numericAmount = parseFloat(amount) || 0;

	const eventInventoryAll = useAppSelector((state) => state.pricing.eventInventoryAll);
	const selectedInventoryIds = useAppSelector((state) => state.pricing.selection.inventoryItemIds);
	const inventoryItems = _.filter(eventInventoryAll.data, (x) => selectedInventoryIds.includes(x.Item_ID));
	const loading = useAppSelector((state) => state.pricing.updateListingPrices.loading);

	const sortedData = _.sortBy(eventInventoryAll.data, "Listing_ID");
	const groupedData = _.groupBy(sortedData, getInventoryGroupKey);

	const rowsData = _.map(groupedData, (value) => {
		const seatNumbers = value.map((x) => x.Seat_Number);
		return {
			...value[0],
			Seats: groupSeatNumbersWithRange(seatNumbers),
			Quantity: value.length,
			Item_Cost: value.reduce((acc, curr) => acc + curr.Item_Cost, 0),
		};
	}).filter((x) => {
		return !x.Invoice_ID;
	});

	const filteredItems = _.filter(rowsData, (x) => selectedInventoryIds.includes(x.Item_ID));
	const sortedByPriceAsc = _.sortBy(filteredItems, "Item_Price");

	const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		let value = e.target.value;
		if (mode === "fixedPrice") value = value.replace(/-/g, "");
		setAmount(value);
	};

	const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (mode === "fixedPrice" && e.key === "-") {
			e.preventDefault();
		}
	};

	const handleAmountBlur = () => {
		const n = parseFloat(amount);
		if (isNaN(n)) {
			setAmount(mode === "fixedPrice" ? "1" : "0");
			return;
		}
		if (mode === "fixedPrice" && n < 1) setAmount("1");
	};

	const handleSpecificAmountChange = (num: number) => {
		setAmount((prev) => {
			const next = (parseFloat(prev) || 0) + num;
			if (mode === "fixedPrice") return String(Math.max(1, next));
			return String(next);
		});
	};

	const buttonProps = (tabMode: TMassPriceUpdateMode) => {
		const isActive = tabMode === mode;

		return {
			variant: isActive ? ButtonVariant.Primary : ButtonVariant.Secondary,
			onClick: () => {
				setMode(tabMode);
				setAmount((prev) => {
					const n = parseFloat(prev);
					if (isNaN(n)) return "1";
					if (tabMode === "fixedPrice") {
						const abs = Math.abs(n);
						return String(abs < 1 ? 1 : abs);
					}
					return String(n);
				});
			},
		};
	};

	const getControllInfoHeadline = () => {
		if (mode === "fixedPrice") {
			return "a fixed amount";
		} else {
			return mode;
		}
	};

	const calculateNewPrice = (price: number) => {
		let newPrice;

		switch (mode) {
			case "fixedPrice": {
				newPrice = numericAmount;
				break;
			}
			case "amount": {
				newPrice = price + numericAmount;
				break;
			}
			default: {
				const onePercent = price * 0.01;
				newPrice = price + onePercent * numericAmount;
			}
		}

		return Math.max(0, newPrice);
	};

	const handleApply = async () => {
		const listingsToUpdate = inventoryItems.map((x) => ({
			listing_id: x.Listing_ID,
			price: calculateNewPrice(x.Item_Price),
			face: null,
		}));

		const response = await dispatch(pricingActions.updateListingPrices(listingsToUpdate));

		if (response.meta.requestStatus === "fulfilled") {
			handleClose?.();
			return;
		}

		toast.warning("Error while trying to update listing price");
	};

	const columns: Array<IColumn<EventInventoryRecord>> = [
		{
			header: "ListingID",
			id: "ListingID",
			accessor: "Listing_ID",
			width: 81,
			sortable: true,
		},
		{
			id: "Section",
			header: "Section",
			accessor: "Section",
			getClassName: () => helperStyles.secondaryText,
			width: 70,
			minWidth: 115,
			sortable: true,
		},
		{
			id: "Row",
			header: "Row",
			accessor: "Row",
			getClassName: () => helperStyles.secondaryText,
			width: 50,
			sortable: true,
		},
		{
			header: "Seats",
			id: "Seat",
			accessor: "Seats",
			valueGetter: ({ rowData }) => rowData.Seats || rowData.Seat_Number,
			formatValue: (x, { rowData }) => x || rowData.Seat_Number,
			getClassName: () => helperStyles.secondaryText,
			width: 75,
			sortable: true,
		},
		{
			header: "Qty",
			id: "Quantity",
			accessor: "Quantity",
			valueGetter: ({ rowData }) => rowData.Quantity || 1,
			formatValue: (x) => x || 1,
			getClassName: () => helperStyles.secondaryText,
			width: 50,
			sortable: true,
		},
		{
			header: "Price",
			id: "Price",
			accessor: "Item_Price",
			formatValue: (x) => <div className={styles.priceContainer}>{formatCurrency(x)}</div>,
			width: 70,
			sortable: true,
			formatTooltip: (x) => {
				return formatCurrency(x);
			},
			isCurrency: true,
		},
		{
			header: "New Price",
			id: "New_Price",
			accessor: "Item_Price",
			formatValue: (x) => <div className={styles.newPriceContainer}>{formatCurrency(calculateNewPrice(x))}</div>,
			width: 90,
			sortable: true,
			formatTooltip: (x) => {
				return formatCurrency(x);
			},
			isCurrency: true,
		},
	];

	return (
		<div className={styles.root}>
			<div className={styles.header}>
				<div aria-hidden="true" style={{ cursor: "pointer" }} onClick={handleClose}>
					<XIconThemed color="#fafafa" />
				</div>
			</div>
			<EventContextHeader
				padding
				listings={filteredItems.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))}
			/>
			<div className={styles.content}>
				<div className={styles.info}>
					<div className={styles.headline}>Mass price update</div>
					<div className={styles.additionalInfo}>
						Apply price updates to all {selectedInventoryIds.length} selected listings: set a fixed price, adjust by dollars
						(+/–), or change by percentage.
					</div>
				</div>
				<div className={styles.tabs}>
					<Button {...buttonProps("amount")}>Update by amount</Button>
					<Button {...buttonProps("percentage")}>Update by percentage</Button>
					<Button {...buttonProps("fixedPrice")}>Set fixed price</Button>
				</div>
				<div className={styles.controls}>
					<div className={styles.controlsInfo}>Update listing price by {getControllInfoHeadline()}</div>
					<div className={styles.controlsContainer}>
						<div className={clx(helperStyles.center, styles.controlsPrefix)}>
							{mode === "amount" || mode === "fixedPrice" ? "$" : "%"}
						</div>
						<div className={styles.inputContainer}>
							<input
								className={styles.input}
								type="number"
								value={amount}
								onChange={handleAmountChange}
								onKeyDown={handleAmountKeyDown}
								onBlur={handleAmountBlur}
							/>
						</div>
						<div className={styles.controlsButtonContainer}>
							<div
								aria-hidden="true"
								onClick={() => handleSpecificAmountChange(-1)}
								className={clx(helperStyles.center, styles.controlButton)}>
								<MinusIconThemed />
							</div>
							<div
								aria-hidden="true"
								onClick={() => handleSpecificAmountChange(1)}
								className={clx(helperStyles.center, styles.controlButton)}>
								<PlusIconThemed />
							</div>
						</div>
					</div>
				</div>
				<div className={styles.listings}>
					<div className={styles.listingsInfo}>Selected listings</div>
					<div className={styles.listingsContainer}>
						<VGrid<EventInventoryRecord> rowsData={sortedByPriceAsc} columns={columns} getRowNodeId={(x) => x.Item_ID} />
					</div>
				</div>
			</div>
			<div className={styles.ctaButtons}>
				<Button
					isLoading={loading}
					disabled={loading}
					style={{ padding: "7.5px 16px", width: 81 }}
					onClick={handleClose}
					variant={ButtonVariant.Secondary}>
					Cancel
				</Button>
				<Button
					isLoading={loading}
					disabled={loading}
					style={{ padding: "7.5px 16px", width: 138 }}
					onClick={handleApply}
					variant={ButtonVariant.Primary}>
					Apply changes
				</Button>
			</div>
		</div>
	);
};

export default MassPriceUpdate;
